import React, { useEffect, useMemo, useState } from "react";
import type { TemplateMeta } from "@/api/templates";
// Fixed: correct import path to the resolver module
import { resolveRenderComponent } from "./resolveRenderComponent";
import PhoneBezel, { getResponsiveScreenHeight } from "./common/PhoneBezel";
import { DEFAULT_COVER } from "@/constants/media";
import { REMOTE_FALLBACK_COVER_URL } from "@/constants/images";

type Props = {
  template: TemplateMeta;
  user: any;
  accent?: string;
  mode?: "grid" | "full";
  className?: string;
  /** Optional: choose preview frame. Defaults to 'phone' (legacy). Use 'none' to match built-in previews. */
  frame?: "phone" | "none";
  /** When rendering dynamic templates, explicitly control inner scroll allowance. */
  allowInnerScroll?: boolean;
  /** Optional ads feed forwarded to renderer (for dynamic templates footer). */
  ads?: any[];
  /** Optional: whether the viewer is the card owner (enables owner-only actions like Broadcast). */
  isOwnerView?: boolean;
  /** Optional: leads list (used by Broadcast modal for audience sizing). */
  leads?: any[];
  /** Optional: callback to submit a captured lead. */
  onLeadSubmit?: (lead: any) => void;
  /** Optional analytics hook. */
  onAnalyticsUpdate?: (...args: any[]) => void;
  /** Optional: callback to open Contact Exchange modal. */
  onContactExchange?: () => void;
};

export const PreviewCard: React.FC<Props> = ({ template, user, accent, mode = "grid", className, frame = "phone", allowInnerScroll, ads, isOwnerView, leads, onLeadSubmit, onAnalyticsUpdate, onContactExchange }) => {
  const Comp = useMemo(
    () => resolveRenderComponent(template),
    // Important: include template.id so different templates with the same renderer/componentKey
    // don't reuse the same wrapped component (which would close over the first template).
    // Also include version for live-updated templates.
    [template.id, template.renderer, template.componentKey, (template as any)?.version]
  );
  // Auto-fetch system ads if not provided (for dynamic previews in admin screens)
  const [fetchedAds, setFetchedAds] = useState<any[] | null>(null);
  useEffect(() => {
    let active = true;
    // We fetch when:
    //  - ads prop is undefined (no ads provided), OR
    //  - ads prop is an empty array AND this looks like a public view (not owner/admin)
    const looksPublic = (() => {
      try {
        if (isOwnerView === true) return false;
        if (typeof window === 'undefined') return false;
        const p = String(window.location?.pathname || '');
        // Heuristic: avoid fetching on obvious admin/studio routes
        if (/\b(admin|studio|dashboard)\b/i.test(p)) return false;
        return true;
      } catch { return false; }
    })();

    const shouldFetch = (ads === undefined) || (Array.isArray(ads) && ads.length === 0 && looksPublic);
    if (shouldFetch) {
      (async () => {
        try {
          const base = (import.meta as any).env?.VITE_APP_BASE_URL || (import.meta as any).env?.VITE_API_BASE_URL || '';
          const url = base
            ? `${String(base).replace(/\/$/, '')}/api/ads?min=3&includePending=1`
            : '/api/ads?min=3&includePending=1';
          const res = await fetch(url);
          if (!active) return;
          if (res.ok) {
            const data = await res.json().catch(() => []);
            if (Array.isArray(data)) setFetchedAds(data);
            else setFetchedAds([]);
          } else {
            setFetchedAds([]);
          }
        } catch {
          setFetchedAds([]);
        }
      })();
    } else {
      setFetchedAds(null);
    }
    return () => { active = false; };
  }, [ads, isOwnerView]);
  // Normalize user media so dynamic templates always have a header image
  const safeUser = useMemo(() => {
    const u: any = { ...(user || {}) };
    // Cover policy (public vs admin):
    // - Public/owner views: if the owner hasn't uploaded a cover, ALWAYS use the system DEFAULT_COVER.
    //   Do NOT fall back to the template's artwork cover.
    // - Admin/template manager views: allow template artwork cover as a fallback when the template defines one.
    const userProvided = (u.coverUrl || u.coverImage) as (string | undefined);
    const tplCover = (template as any)?.coverUrl as (string | undefined);
    // Detect admin contexts heuristically by pathname (no explicit prop available here).
    // This keeps template artwork visible only inside admin screens/template manager previews.
    const isAdminContext = (() => {
      try {
        if (typeof window === 'undefined') return false;
        const p = String(window.location?.pathname || '');
        return /\b(admin|studio|dashboard|template|manager)\b/i.test(p);
      } catch { return false; }
    })();
    const candidate = userProvided && String(userProvided).trim()
      ? String(userProvided).trim()
      : (isAdminContext ? (tplCover && String(tplCover).trim()) : DEFAULT_COVER) || DEFAULT_COVER;
    // Prefer local default, then remote fallback (handled inside renderers on error)
    const resolvedCover = typeof candidate === 'string' && candidate.trim() !== '' ? candidate : DEFAULT_COVER;
    u.coverUrl = resolvedCover;
    if (!u.coverImage) u.coverImage = resolvedCover;
    // Normalize avatar field commonly used by templates
    if (!u.avatarUrl && u.avatar) u.avatarUrl = u.avatar;
    // Ensure avatar uses absolute or known-root path so it renders in all modes
    try {
      const s = String(u.avatarUrl || '').trim();
      if (s && !/^(https?:\/\/|blob:|data:)/i.test(s)) {
        if (s.startsWith('/images/') || s.startsWith('/assets/') || s.startsWith('/uploads/')) {
          // ok
        } else if (s.startsWith('uploads/')) {
          u.avatarUrl = `/${s}`;
        } else {
          const base = (import.meta as any).env?.VITE_APP_BASE_URL || (import.meta as any).env?.VITE_API_BASE_URL || '';
          if (base) u.avatarUrl = `${String(base).replace(/\/$/, '')}/${s.replace(/^\//,'')}`;
        }
      }
    } catch {}
    return u;
  }, [user, template, isOwnerView]);
  // Only use a fixed height for grid-mode phone frames. Full/live previews should auto-size.
  const screenHeight = (frame === 'phone' && mode === 'grid') ? getResponsiveScreenHeight(mode) : undefined;
  // If no frame requested, render directly. Use fixed height only for grid (selection panel).
  if (frame === "none") {
    if (mode === 'grid') {
      // In grid mode, prefer filling the available space; if no explicit height provided,
      // fall back to 100% so parent containers like the admin preview panel can drive sizing.
      const gridHeight: any = (typeof screenHeight === 'number' ? screenHeight : '100%');
      return (
        <div className={className} style={{ height: gridHeight }}>
          <div className="mx-auto" style={{ width: "100%", maxWidth: 360, height: "100%" }}>
            <div className="rounded-3xl shadow-2xl ring-1 ring-black/5 bg-white overflow-hidden" style={{ height: "100%" }}>
              <Comp template={template} user={safeUser} accent={accent} mode={mode} allowInnerScroll={allowInnerScroll} ads={ads === undefined ? fetchedAds ?? undefined : ads} isOwnerView={isOwnerView} leads={leads} onLeadSubmit={onLeadSubmit} onAnalyticsUpdate={onAnalyticsUpdate} onContactExchange={onContactExchange} />
            </div>
          </div>
        </div>
      );
    }
    // Full/live previews: let height grow naturally; rely on parent to set width caps.
    return (
      <div className={className}>
        <div className="mx-auto" style={{ width: "100%" }}>
          {/* Allow content to fully display without cropping in full/live previews */}
          <div className="rounded-3xl shadow-2xl ring-1 ring-black/5 bg-white">
            <Comp template={template} user={safeUser} accent={accent} mode={mode} allowInnerScroll={allowInnerScroll} ads={ads === undefined ? fetchedAds ?? undefined : ads} isOwnerView={isOwnerView} leads={leads} onLeadSubmit={onLeadSubmit} onAnalyticsUpdate={onAnalyticsUpdate} onContactExchange={onContactExchange} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <PhoneBezel className={className} screenHeight={screenHeight}
    >
      <Comp template={template} user={safeUser} accent={accent} mode={mode} allowInnerScroll={allowInnerScroll} ads={ads === undefined ? fetchedAds ?? undefined : ads} isOwnerView={isOwnerView} leads={leads} onLeadSubmit={onLeadSubmit} onAnalyticsUpdate={onAnalyticsUpdate} onContactExchange={onContactExchange} />
    </PhoneBezel>
  );
};
